export interface TourStep {
  id: string;
  title: string;
  body: string;
  icon: string;
  target?: string; // CSS selector to highlight
  href?: string; // "Go there" link
  hrefLabel?: string;
  plan?: string; // badge label when gated
}

const TOUR_KEY = "syncpro_tour_done";

export const TOUR_STEPS: TourStep[] = [
  {
    id: "welcome",
    icon: "👋",
    title: "Welcome to your workspace!",
    body: "This quick tour shows you where everything lives. It only takes 30 seconds — you can always reopen it from the ? button.",
  },
  {
    id: "dashboard",
    icon: "📊",
    title: "Dashboard",
    body: "Your command centre. See open tickets, SLA health, team workload, and activity at a glance.",
    href: "/",
    hrefLabel: "View dashboard",
    target: '[data-tour="dashboard"]',
  },
  {
    id: "inbox",
    icon: "📥",
    title: "Universal Inbox",
    body: "All incoming requests — email, Slack, portal, API — land in one unified inbox so nothing slips through.",
    href: "/inbox",
    hrefLabel: "Open inbox",
    target: '[data-tour="inbox"]',
  },
  {
    id: "tickets",
    icon: "🎫",
    title: "Internal Ticketing",
    body: "Create, assign, and track internal requests with SLA rules, workflow routing, and full audit trails.",
    href: "/tickets",
    hrefLabel: "View tickets",
    target: '[data-tour="internal"]',
  },
  {
    id: "workflows",
    icon: "⚙️",
    title: "Ticket Workflow Builder",
    body: "Design approval flows and routing rules visually. Map how requests move from open to resolved across your teams.",
    href: "/admin/ticket-workflows",
    hrefLabel: "Open workflow builder",
    target: '[data-tour="workflows"]',
    plan: "PRO",
  },
  {
    id: "kb",
    icon: "📖",
    title: "Knowledge Base",
    body: "Internal docs, policies, and guides in one searchable place. Keep your team's collective knowledge organised.",
    href: "/knowledge-base",
    hrefLabel: "Browse knowledge base",
    target: '[data-tour="kb"]',
  },
  {
    id: "admin",
    icon: "⚙️",
    title: "Admin & Settings",
    body: "Manage your team, departments, roles, integrations, and branding. You'll set most of this up once and rarely revisit it.",
    href: "/admin/user-management",
    hrefLabel: "Open settings",
    target: '[data-tour="admin"]',
  },
  {
    id: "done",
    icon: "🚀",
    title: "You're ready to go!",
    body: "Explore at your own pace. Hit the ? button in the nav to restart this tour any time. Invite your team from Admin → People.",
    href: "/admin/user-management",
    hrefLabel: "Invite your team →",
  },
];

export function useTour() {
  const step = ref(0);

  function startTour() {
    step.value = 0;
  }

  function nextStep() {
    if (step.value < TOUR_STEPS.length - 1) {
      step.value++;
    } else {
      endTour();
    }
  }

  function prevStep() {
    if (step.value > 0) step.value--;
  }

  function endTour() {
    if (import.meta.client) {
      localStorage.setItem(TOUR_KEY, "1");
    }
  }

  function maybeAutoStart() {
    if (!import.meta.client) return;
    if (!localStorage.getItem(TOUR_KEY)) {
      startTour();
    }
  }

  const currentStep = computed(() => TOUR_STEPS[step.value]);
  const isLastStep = computed(() => step.value === TOUR_STEPS.length - 1);
  const progress = computed(() =>
    Math.round(((step.value + 1) / TOUR_STEPS.length) * 100),
  );

  return {
    step,
    currentStep,
    isLastStep,
    progress,
    startTour,
    nextStep,
    prevStep,
    endTour,
    maybeAutoStart,
  };
}
